import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'

interface ProtectedRouteProps {
    children: React.ReactNode
    requiredRole?: string
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
    const { isAuthenticated, isLoading, user } = useAuth()
    const location = useLocation()
    const { t } = useTranslation()

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="flex flex-col items-center gap-4" role="status" aria-live="polite">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    <span className="text-gray-600 dark:text-gray-400 text-lg">{t('common.loading', 'Loading...')}</span>
                </div>
            </div>
        )
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />
    }

    if (requiredRole && user?.role !== requiredRole) {
        if (requiredRole === 'Admin' && user?.role !== 'Admin') {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-red-500 mb-4">403</h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg">{t('common.forbidden', "You don't have permission to access this page.")}</p>
                    </div>
                </div>
            )
        }
    }

    return <>{children}</>
}

export default ProtectedRoute
